import React, { useEffect, useState } from "react";
import "./patientReceiptStyles.css";
import converter from "number-to-words";
import { useQuery } from "@tanstack/react-query";
import { fetchPatientReceiptData } from "services/patientappointmentdetails";
import { DEFAULT_TOTAL_RECORDS, STALE_TIME } from "utils/constants";
import usePagination from "hooks/usePagination";
import Loader from "components/Common/loader";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import autoTable from "jspdf-autotable";

const PatientReceipt = () => {
  const { currentPage, updateTotalRecords } = usePagination();
  const [billAmt, setBillAmt] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const receiptData = JSON.parse(localStorage.getItem("receiptPatient"));

  const formatUnixTimestamp = (timestamp) => {
    const date = new Date(timestamp * 1000);
    const options = { day: "2-digit", month: "short", year: "numeric" };
    return date.toLocaleDateString("en-GB", options).replace(",", "");
  };

  function convertEpochToDate(epoch) {
    if (!epoch) return "";
    const date = new Date(epoch * 1000);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  function getDaysFromEpoch(epoch, options = {}) {
    if (!epoch || isNaN(Number(epoch))) {
      return options.defaultValue !== undefined ? options.defaultValue : 0;
    }

    const inputDate = new Date(Number(epoch) * 1000);
    const currentDate = new Date();

    const inputDateStart = new Date(
      inputDate.getFullYear(),
      inputDate.getMonth(),
      inputDate.getDate()
    );

    const currentDateStart = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate()
    );

    const diffDays = Math.ceil(
      (inputDateStart - currentDateStart) / (1000 * 60 * 60 * 24)
    );

    return diffDays;
  }

  const {
    data: cashReceiptData,
    isLoading,
    refetch,
  } = useQuery(
    ["fetch-cash-patient-receipt"],
    () =>
      fetchPatientReceiptData({
        appointment_id: receiptData,
      }),
    {
      staleTime: STALE_TIME,
      onSuccess: (res) => {
        updateTotalRecords(res?.data?.additional_data?.total_count);
      },
      select: (res) => {
        const response = res?.data?.data;
        if (response && response.length) {
          res.data.data = response.map((x, index) => ({
            ...x,
            _index: (currentPage - 1) * DEFAULT_TOTAL_RECORDS + index + 1,
          }));
        }
        return res;
      },
      onError: (res) => {
        console.error("Error fetching payments:", res);
      },
      enabled: !!receiptData,
    }
  );

  useEffect(() => {
    if (cashReceiptData && cashReceiptData.data[0]) {
      const appointment_investigation_obj =
        cashReceiptData.data[0].appointment_investigation_obj;

      const uniquePackages = {};
      let noPackageTotal = 0;

      appointment_investigation_obj.forEach((item) => {
        const packageId = item.package_id;
        const cost = item.package?.package_cost || item.mrp || 0;

        if (packageId) {
          if (!uniquePackages[packageId]) {
            uniquePackages[packageId] = cost;
          }
        } else {
          noPackageTotal += cost;
        }
      });

      const packageTotal = Object.values(uniquePackages).reduce(
        (sum, cost) => sum + cost,
        0
      );

      const totalBillAmt = packageTotal + noPackageTotal;
      setBillAmt(totalBillAmt);
    }
  }, [cashReceiptData]);

  if (!cashReceiptData || isLoading) {
    return <Loader isLoading={isLoading} />;
  }

  function convertSecondsToAge(seconds) {
    const secondsInYear = 31536000;
    const secondsInMonth = 2629746;
    const secondsInDay = 86400;

    const years = Math.floor(seconds / secondsInYear);
    seconds %= secondsInYear;

    const months = Math.floor(seconds / secondsInMonth);
    seconds %= secondsInMonth;

    const days = Math.floor(seconds / secondsInDay);

    return { years, months, days };
  }

  const {
    appointment_detail_obj = [],
    appointment_investigation_obj = [],
    franchise_obj = {},
    patient_obj = {},
    payment_mode_obj = {},
    appointment_obj = {},
    zone_obj = {},
    refer_doctor_obj = {},
    formattedDate = "",
    uhid = "",
    service_code = "",
    service_name = "",
    department_name = "",
    bill_amount = "",
    discount = "",
    paid_amount = "",
    due_amount = "",
    referred_by = "",
    center = "",
    date_of_visit = "",
    amount_after_discount = 0,
  } = cashReceiptData?.data[0] || {};

  const ageDetails = convertSecondsToAge(patient_obj?.age || 0);

  const capitalizeWords = (str) => {
    return str.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const downloadPDF = async () => {
    try {
      setIsDownloading(true);

      const downloadBtn = document.querySelector(".download-btn");
      if (downloadBtn) downloadBtn.style.display = "none";

      const receiptElement = document.querySelector(".receipt-inner-unique");
      if (!receiptElement) throw new Error("Receipt element not found");

      const canvas = await html2canvas(receiptElement, {
        scale: 4,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png", 1.0);
      const pdf = new jsPDF("p", "mm", "a4");

      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = 210;
      const pdfHeight = 297;
      const imgWidth = pdfWidth;
      const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      const patientName = patient_obj?.patient_name || "Patient";
      const currentDate = new Date().toISOString().split("T")[0];
      const filename = `Receipt_${patientName.replace(
        /\s+/g,
        "_"
      )}_${currentDate}.pdf`;

      pdf.save(filename);

      alert("PDF downloaded successfully!");
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");
    } finally {
      setIsDownloading(false);
      const downloadBtn = document.querySelector(".download-btn");
      if (downloadBtn) downloadBtn.style.display = "block";
    }
  };

  const groupedData = appointment_investigation_obj.reduce(
    (acc, item, index) => {
      const packageId = item.package_id;

      if (packageId) {
        if (!acc[packageId]) {
          acc[packageId] = {
            package: item.package,
            investigations: [],
            departments: new Set(),
            delivery_times: new Set(),
            package_cost: item.package?.package_cost || item.mrp || 0,
          };
        }

        acc[packageId].investigations.push(item.investigation);
        if (item.investigation?.lab_department?.department) {
          acc[packageId].departments.add(
            item.investigation.lab_department.department
          );
        }
        if (item.delivery_time) {
          acc[packageId].delivery_times.add(
            convertEpochToDate(item.delivery_time)
          );
        }
      } else {
        const uniqueKey = `no_package_${index}`;
        acc[uniqueKey] = {
          package: null,
          investigations: [item.investigation],
          departments: new Set(
            item.investigation?.lab_department?.department
              ? [item.investigation.lab_department.department]
              : []
          ),
          delivery_times: new Set(
            item.delivery_time ? [convertEpochToDate(item.delivery_time)] : []
          ),
          package_cost: item.mrp || 0,
        };
      }

      return acc;
    },
    {}
  );

  return (
    <div className="receipt-container-unique">
      <div className="receipt-wrapper-unique">
        <div className="receipt-inner-unique">
          <div className="d-flex align-items-center mb-2">
            <div className="logo col-4">
              <img
                src="../assets/img/lims_logo.png"
                alt="Lims"
                width={140}
                height={90}
              />
            </div>
            <div className="receipt-header-unique col-8 d-flex justify-content-between align-items-center">
              BILL CUM CASH RECEIPT
            </div>
          </div>
          <hr />
          <div className="row p-2">
            <div className="col-md-6 col-6">
              <div className="receipt-detail-unique">
                Name: <span>{patient_obj?.patient_name || ""}</span>
              </div>
              <div className="receipt-detail-unique">
                Age/Gender:{" "}
                <span>
                  {ageDetails?.years} Y / {patient_obj?.gender || ""}
                </span>
              </div>
              <div className="receipt-detail-unique">
                Contact No.: <span>{patient_obj?.mobile || ""}</span>
              </div>
              <div className="receipt-detail-unique">
                UHID: <span>{uhid}</span>
              </div>
              <div className="receipt-detail-unique">
                Client Name: <span>{patient_obj?.patient_name || ""}</span>
              </div>
              <div className="receipt-detail-unique">
                Address: <span>{patient_obj?.address || ""}</span>
              </div>
            </div>
            <div className="col-6 col-md-6">
              <div className="receipt-detail-unique">
                Visit No.: <span>{patient_obj?.registeration_no || ""}</span>
              </div>
              <div className="receipt-detail-unique">
                Visit/Reg. Date:{" "}
                <span>{formatUnixTimestamp(patient_obj?.generated)}</span>
              </div>
              <div className="receipt-detail-unique">
                Referred By: <span>{refer_doctor_obj?.doctor_name}</span>
              </div>
              <div className="receipt-detail-unique">
                Bill: <span>{payment_mode_obj?.payment_mode || ""}</span>
              </div>
              <div className="receipt-detail-unique">
                Center: <span>{zone_obj?.zone_name}</span>
              </div>
            </div>
          </div>

          {/* <hr style={{ margin: "0px" }} /> */}

          <div style={{ overflowX: "auto" }}>
            <table
              className="receipt-table-unique"
              style={{
                width: "100%",
                borderCollapse: "collapse",
                textAlign: "left",
                fontSize: "13px",
              }}
            >
              <thead>
                <tr className="receipt-colheaders-unique">
                  <th
                    style={{
                      whiteSpace: "nowrap",
                      padding: "8px",
                      minWidth: "40px",
                    }}
                  >
                    #
                  </th>
                  <th
                    style={{
                      whiteSpace: "nowrap",
                      padding: "8px",
                      minWidth: "100px",
                    }}
                  >
                    Service Code
                  </th>
                  <th
                    style={{
                      whiteSpace: "nowrap",
                      padding: "8px",
                      minWidth: "200px",
                    }}
                  >
                    Service Name
                  </th>
                  <th
                    style={{
                      whiteSpace: "nowrap",
                      padding: "8px",
                      minWidth: "120px",
                    }}
                  >
                    Department
                  </th>
                  <th
                    style={{
                      whiteSpace: "nowrap",
                      padding: "8px",
                      minWidth: "110px",
                    }}
                  >
                    Delivery Date
                  </th>
                  <th
                    style={{
                      whiteSpace: "nowrap",
                      padding: "8px",
                      minWidth: "80px",
                    }}
                  >
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(groupedData).map(([packageId, data], index) => (
                  <tr key={packageId} className="hover:bg-gray-50">
                    <td
                      className="border border-gray-300 px-4 py-2"
                      style={{ verticalAlign: "top", textAlign: "start" }}
                    >
                      {index + 1}
                    </td>
                    <td
                      className="border border-gray-300 px-4 py-2"
                      style={{ verticalAlign: "top", textAlign: "start" }}
                    >
                      {data.investigations
                        .map((inv) => inv.investigation_code)
                        .join(", ") || ""}
                    </td>
                    <td
                      className="border border-gray-300 px-4 py-2"
                      style={{ verticalAlign: "top", textAlign: "start" }}
                    >
                      <div>
                        <strong>{data.package?.package_name || ""}</strong>
                        <span className="text-sm text-gray-600">
                          (
                          {data.investigations
                            .map((inv) => inv.investigation_name)
                            .join(", ")}
                          )
                        </span>
                      </div>
                    </td>
                    <td
                      className="border border-gray-300 px-4 py-2"
                      style={{ verticalAlign: "top", textAlign: "start" }}
                    >
                      {Array.from(data.departments).join(", ") || ""}
                    </td>
                    <td
                      className="border border-gray-300 px-4 py-2"
                      style={{ verticalAlign: "top", textAlign: "start" }}
                    >
                      {Array.from(data.delivery_times).join(", ") || ""}
                    </td>
                    <td
                      className="border border-gray-300 px-4 py-2"
                      style={{ verticalAlign: "top", textAlign: "start" }}
                    >
                      {data.package_cost || ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="totals-container-unique mt-3">
            <div>
              Bill Amount: <span>{billAmt || 0}</span>
            </div>
            <div>
              Coll. Charges:{" "}
              <span>{appointment_detail_obj[0]?.collection_charge}</span>
            </div>
            <div>
              Total Discount:{" "}
              <span>{appointment_detail_obj[0]?.discount_amount || 0}</span>
            </div>
            <div>
              Total Paid Amount:{" "}
              <span>
                {appointment_detail_obj[0]?.amount_after_discount -
                  appointment_detail_obj[0]?.balance_amount +
                  appointment_detail_obj[0]?.collection_charge}
              </span>
            </div>
            <div>
              Due Amount:{" "}
              <span>{appointment_detail_obj[0]?.balance_amount}</span>
            </div>
          </div>
          <div className="receipt-total-unique">
            Received with thanks:{" "}
            {billAmt ? capitalizeWords(converter.toWords(billAmt)) : "Zero"}
          </div>

          {/* <hr /> */}
          <div className="d-flex justify-content-between">
            <div className="d-flex justify-content-center align-items-center">
              <strong>Notes:-</strong>{" "}
              <span style={{ fontSize: "12px", marginLeft: "5px" }}>
                This is computer generated Bill/Receipt hence does not required
                any seal or signature.
              </span>
            </div>
            <div
              className="receipt-generated-unique"
              style={{ textAlign: "center" }}
            >
              Generated by LIMS ERP
            </div>
          </div>

          <div className="d-flex justify-content-center mt-3">
            <button
              className="btn download-btn"
              style={{
                background: isDownloading ? "#6c757d" : "#11598f",
                color: "#fff",
                cursor: isDownloading ? "not-allowed" : "pointer",
              }}
              onClick={downloadPDF}
              disabled={isDownloading}
            >
              {isDownloading ? "Generating PDF..." : "Download PDF"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientReceipt;
